/**
 * External article curation for AI Central
 *
 * This file contains curated external articles and blog posts for the AI Central reading list.
 * To add new articles, simply add objects to the appropriate category array.
 */

export interface ExternalArticle {
  title: string;
  author?: string;
  source: string;
  date: string;
  excerpt: string;
  url: string;
  tags: string[];
  featured?: boolean; // Set to true to show on main AI Central page
}

export interface ArticleCategory {
  name: string;
  description: string;
  articles: ExternalArticle[];
}

export interface ModelRelease {
  title: string;
  provider: string;
  releaseDate: string;
  url: string;
  highlights: string[];
}

/**
 * Latest model releases
 * Manually curated list of recent major LLM releases
 * Update quarterly or when significant releases occur
 */
export const latestReleases: ModelRelease[] = [
  {
    title: 'Claude Opus 4.6',
    provider: 'Anthropic',
    releaseDate: '2026-02-05',
    url: 'https://www.anthropic.com/news/claude-opus-4-6',
    highlights: ['Extended 1M token context window', 'Enhanced agentic capabilities', 'Improved code generation and debugging'],
  },
  {
    title: 'GPT-5.2',
    provider: 'OpenAI',
    releaseDate: '2025-12-11',
    url: 'https://openai.com/index/introducing-gpt-5-2/',
    highlights: ['Advanced reasoning and planning', 'Native vision and audio understanding', 'Reduced latency and cost'],
  },
  {
    title: 'GPT-5.3 Codex',
    provider: 'OpenAI',
    releaseDate: '2026-02-05',
    url: 'https://openai.com/index/introducing-gpt-5-3-codex/',
    highlights: ['Specialized for code generation', 'Multi-language refactoring', 'Integrated testing and debugging'],
  },
  {
    title: 'Gemini 3 Pro',
    provider: 'Google',
    releaseDate: '2025-11-18',
    url: 'https://deepmind.google/models/gemini/pro/',
    highlights: ['3M token context window', 'Real-time multimodal processing', 'Advanced research capabilities'],
  },
  {
    title: 'Gemini 3 Flash',
    provider: 'Google',
    releaseDate: '2025-12-17',
    url: 'https://blog.google/products-and-platforms/products/gemini/gemini-3-flash/',
    highlights: ['Frontier intelligence built for speed', 'Lower cost at scale', 'Global availability'],
  },
  {
    title: 'Claude Haiku 4.5',
    provider: 'Anthropic',
    releaseDate: '2025-10-15',
    url: 'https://www.anthropic.com/news/claude-haiku-4-5',
    highlights: ['Near-frontier coding performance', 'More than twice the speed', 'Optimized for low-latency tasks'],
  },
  {
    title: 'Claude Sonnet 4.5',
    provider: 'Anthropic',
    releaseDate: '2025-09-29',
    url: 'https://www.anthropic.com/news/claude-sonnet-4-5',
    highlights: ['Best-in-class coding performance', 'Stronger agentic capabilities', 'Improved reasoning and math'],
  },
];

/**
 * Featured articles that appear on the main AI Central page
 * Limit to 2-3 articles for optimal display
 */
export const featuredArticles: ExternalArticle[] = [
  {
    title: 'Effective Context Engineering for AI Agents',
    author: 'Anthropic',
    source: 'Anthropic Engineering Blog',
    date: 'January 2026',
    excerpt: 'Learn how to effectively engineer context for AI agents to maximize their capabilities and performance in production environments.',
    url: 'https://www.anthropic.com/engineering/effective-context-engineering-for-ai-agents',
    tags: ['Agents', 'Context Engineering', 'Best Practices'],
    featured: true,
  },
  {
    title: 'Build with Claude - Prompt Engineering Overview',
    author: 'Anthropic',
    source: 'Claude Platform Documentation',
    date: 'Updated January 2026',
    excerpt: 'Comprehensive guide to prompt engineering best practices and strategies for building effective AI applications with Claude.',
    url: 'https://platform.claude.com/docs/en/build-with-claude/prompt-engineering/overview',
    tags: ['Prompts', 'Claude', 'Best Practices'],
    featured: true,
  },
];

/**
 * All external articles organized by category
 * These appear on the dedicated reading list TechDocs page
 */
export const articleCategories: ArticleCategory[] = [
  {
    name: 'AI Agents & Agentic Systems',
    description: 'Building intelligent, autonomous AI systems',
    articles: [
      {
        title: 'Effective Context Engineering for AI Agents',
        author: 'Anthropic',
        source: 'Anthropic Engineering Blog',
        date: 'January 2026',
        excerpt: 'Learn how to effectively engineer context for AI agents to maximize their capabilities and performance in production environments.',
        url: 'https://www.anthropic.com/engineering/effective-context-engineering-for-ai-agents',
        tags: ['Agents', 'Context Engineering', 'Best Practices'],
        featured: true,
      },
      {
        title: 'Building an Agentic Memory System for GitHub Copilot',
        author: 'GitHub',
        source: 'GitHub Blog',
        date: 'January 15, 2026',
        excerpt: 'Discover how to build robust memory systems for AI agents, with real-world examples from GitHub Copilot\'s architecture.',
        url: 'https://github.blog/ai-and-ml/github-copilot/building-an-agentic-memory-system-for-github-copilot/',
        tags: ['Agents', 'Memory Systems', 'Architecture'],
      },
      {
        title: 'About Agent Skills - GitHub Copilot Concepts',
        author: 'GitHub',
        source: 'GitHub Documentation',
        date: 'Updated January 2026',
        excerpt: 'Understand how agent skills work in GitHub Copilot and how to design agents with specialized capabilities.',
        url: 'https://docs.github.com/en/copilot/concepts/agents/about-agent-skills',
        tags: ['Agents', 'Skills', 'GitHub Copilot'],
      },
    ],
  },
  {
    name: 'Prompt Engineering & Best Practices',
    description: 'Crafting effective prompts and engineering practices',
    articles: [
      {
        title: 'Build with Claude - Prompt Engineering Overview',
        author: 'Anthropic',
        source: 'Claude Platform Documentation',
        date: 'Updated January 2026',
        excerpt: 'Comprehensive guide to prompt engineering best practices and strategies for building effective AI applications with Claude.',
        url: 'https://platform.claude.com/docs/en/build-with-claude/prompt-engineering/overview',
        tags: ['Prompts', 'Claude', 'Best Practices'],
        featured: true,
      },
      {
        title: 'Spec-Driven Development: Unpacking 2025 New Engineering Practices',
        author: 'ThoughtWorks',
        source: 'ThoughtWorks Insights',
        date: 'December 04, 2025',
        excerpt: 'Explore how specification-driven development is transforming modern engineering practices and enabling more reliable AI systems.',
        url: 'https://www.thoughtworks.com/en-us/insights/blog/agile-engineering-practices/spec-driven-development-unpacking-2025-new-engineering-practices',
        tags: ['Best Practices', 'Engineering', 'Agile'],
      },
    ],
  },
  {
    name: 'AI Development & Engineering',
    description: 'Engineering patterns and development strategies for AI systems',
    articles: [
      {
        title: 'Effective Context Engineering for AI Agents',
        author: 'Anthropic',
        source: 'Anthropic Engineering Blog',
        date: 'January 2026',
        excerpt: 'Discover techniques for engineering effective context that maximizes AI agent performance and reliability.',
        url: 'https://www.anthropic.com/engineering/effective-context-engineering-for-ai-agents',
        tags: ['Context', 'Engineering', 'Agents'],
      },
    ],
  },
];

/**
 * Helper function to get all articles (useful for search/filtering)
 */
export const getAllArticles = (): ExternalArticle[] => {
  return articleCategories.flatMap(category => category.articles);
};

/**
 * Helper function to get articles by tag
 */
export const getArticlesByTag = (tag: string): ExternalArticle[] => {
  return getAllArticles().filter(article =>
    article.tags.some(t => t.toLowerCase() === tag.toLowerCase())
  );
};
